import { useEffect } from "react";
import {
	Dimensions,
	TouchableWithoutFeedback,
	type ViewStyle,
} from "react-native";
import Animated, {
	useSharedValue,
	useAnimatedStyle,
	withTiming,
	cancelAnimation,
} from "react-native-reanimated";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

const { width: screenWidth } = Dimensions.get("window");

type Props = {
	visible: boolean;
	handleClose?: () => void;
	children: React.ReactNode;
	variant?: "permanent" | "persistent" | "temporary";
	drawerWidth?: number | string;
};

export const ChatDrawer = ({
	visible,
	handleClose,
	children,
	variant = "temporary",
	drawerWidth = 550,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	//TODO:Migrate to expo-drawer since this is a feature component. need to migrate all to use expo-router. for now
	// let's just use the drawer from react native paper

	const theme = useAppTheme();
	const isPercentage =
		typeof drawerWidth === "string" && drawerWidth.endsWith("%");
	const numericWidth =
		typeof drawerWidth === "string"
			? isPercentage
				? (Number.parseFloat(drawerWidth) / 100) * screenWidth
				: screenWidth
			: drawerWidth;
	const widthValue: number | string =
		typeof drawerWidth === "string" && isPercentage
			? drawerWidth
			: numericWidth;
	const translateX = useSharedValue(visible ? 0 : numericWidth);
	const backdropOpacity = useSharedValue(visible ? 1 : 0);
	const animatedWidth = useSharedValue(visible ? numericWidth : 0);

	useEffect(() => {
		translateX.value = withTiming(visible ? 0 : numericWidth, {
			duration: 300,
		});
		backdropOpacity.value = withTiming(visible ? 1 : 0, { duration: 300 });
		animatedWidth.value = withTiming(visible ? numericWidth : 0, {
			duration: 300,
		});

		return () => {
			cancelAnimation(translateX);
			cancelAnimation(backdropOpacity);
			cancelAnimation(animatedWidth);
		};
	}, [visible, numericWidth, translateX, backdropOpacity, animatedWidth]);

	const drawerStyle = useAnimatedStyle(() => ({
		transform: [{ translateX: translateX.value }],
		width: typeof widthValue === "string" ? numericWidth : widthValue,
	}));

	const backdropStyle = useAnimatedStyle(() => ({
		opacity: backdropOpacity.value,
	}));

	const persistentDrawerStyle = useAnimatedStyle(() => ({
		width: animatedWidth.value,
		overflow: "hidden" as const,
	}));

	if (variant === "permanent") {
		const permanentWidth = visible ? widthValue : 0;
		return (
			<Box
				width={permanentWidth as ViewStyle["width"]}
				backgroundColor={theme.colors.surface}
				style={{
					borderLeftWidth: 1,
					borderLeftColor: theme.palette.divider,
				}}
			>
				{children}
			</Box>
		);
	}

	if (variant === "persistent") {
		return (
			<Animated.View
				style={[
					{
						backgroundColor: theme.colors.surface,
						borderLeftWidth: visible ? 1 : 0,
						borderLeftColor: theme.palette.divider,
					},
					persistentDrawerStyle,
				]}
			>
				<Box
					width={numericWidth}
					height="100%"
					backgroundColor={theme.colors.surface}
				>
					{children}
				</Box>
			</Animated.View>
		);
	}

	return (
		<>
			{visible && (
				<TouchableWithoutFeedback onPress={handleClose}>
					<Animated.View
						style={[
							{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: theme.colors.scrim,
							},
							backdropStyle,
						]}
					/>
				</TouchableWithoutFeedback>
			)}
			<Animated.View
				style={[
					{
						position: "absolute",
						top: 0,
						right: 0,
						bottom: 0,
						backgroundColor: theme.colors.surface,
						borderLeftWidth: 1,
						borderLeftColor: theme.palette.divider,
						shadowColor: theme.colors.shadow,
						shadowOffset: { width: -2, height: 0 },
						shadowOpacity: 0.1,
						shadowRadius: 8,
						elevation: 2,
					},
					drawerStyle,
				]}
			>
				{children}
			</Animated.View>
		</>
	);
};
